'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';

const policyContent = [
  {
    id: 'information-collection',
    title: 'جمع المعلومات',
    content: `نقوم بجمع البيانات الشخصية بما في ذلك اسمك وعنوان بريدك الإلكتروني ورقم هاتفك عند التسجيل في منصتنا أو الاتصال بنا. بالإضافة إلى ذلك، قد نجمع معلومات غير شخصية حول تفاعلاتك مع خدماتنا، مثل معلومات الجهاز ونوع المتصفح وسلوك التصفح.`,
  },
  {
    id: 'use-of-information',
    title: 'استخدام المعلومات',
    content: `تُستخدم المعلومات التي نجمعها لتقديم خدماتنا وتحسينها، والرد على استفساراتك، وضمان أمان منصتنا، والتواصل معك بشأن التحديثات والنشرات الإخبارية (إذا كنت قد اشتركت في تلقيها).`,
  },
  {
    id: 'data-security',
    title: 'أمان البيانات',
    content: `نأخذ أمان معلوماتك الشخصية على محمل الجد. نستخدم التشفير وإجراءات الأمان المتوافقة مع معايير الصناعة لحماية بياناتك من الوصول غير المصرح به أو الكشف عنها أو تغييرها. ومع ذلك، لا توجد طريقة نقل عبر الإنترنت أو تخزين إلكتروني آمنة تمامًا، ولا يمكننا ضمان الأمان المطلق.`,
  },
  {
    id: 'data-retention',
    title: 'الاحتفاظ بالبيانات',
    content: `نحتفظ بمعلوماتك الشخصية طالما كان ذلك ضروريًا لتحقيق الأغراض المبينة في سياسة الخصوصية هذه، ما لم يكن القانون يتطلب أو يسمح بفترة احتفاظ أطول. على سبيل المثال، نحتفظ بمعلومات الحساب طالما كان حسابك نشطًا. عندما لا تكون لدينا حاجة تجارية مشروعة لمعالجة معلوماتك الشخصية، فسنقوم إما بحذفها أو إخفاء هويتها.`,
  },
  {
    id: 'data-sharing',
    title: 'مشاركة البيانات',
    content: `لا نشارك أو نبيع أو نتبادل معلوماتك الشخصية مع أطراف ثالثة لأغراض تسويقية. قد نشارك بياناتك مع مزودي خدمات موثوقين يساعدوننا في تشغيل موقعنا الإلكتروني وخدماتنا، بشرط موافقتهم على الحفاظ على سرية معلوماتك.`,
  },
  {
    id: 'cookies',
    title: 'ملفات تعريف الارتباط وتقنيات التتبع',
    content: `نستخدم ملفات تعريف الارتباط وتقنيات التتبع المماثلة لتحسين تجربتك على منصتنا. تساعدنا هذه التقنيات في تحليل حركة المرور على الموقع، وتذكر تفضيلاتك، وتقديم محتوى مخصص. يمكنك التحكم في ملفات تعريف الارتباط من خلال إعدادات متصفحك، ولكن تعطيلها قد يؤثر على وظائف بعض الميزات.`,
  },
  {
    id: 'third-party-links',
    title: 'روابط الأطراف الثالثة',
    content: `قد يحتوي موقعنا الإلكتروني على روابط لمواقع خارجية. نحن غير مسؤولين عن ممارسات الخصوصية أو محتوى هذه المواقع التابعة لأطراف ثالثة. نشجعك على مراجعة سياسات الخصوصية الخاصة بها قبل تقديم أي معلومات شخصية.`,
  },
  {
    id: 'your-rights',
    title: 'حقوقك',
    content: `لديك الحق في الوصول إلى معلوماتك الشخصية أو تحديثها أو حذفها في أي وقت. إذا كنت ترغب في ممارسة هذه الحقوق أو لديك أي مخاوف بشأن بياناتك، يرجى الاتصال بنا باستخدام المعلومات المقدمة أدناه.`,
  },
  {
    id: 'policy-changes',
    title: 'التغييرات على هذه السياسة',
    content: `قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر أي تغييرات على هذه الصفحة، وسنقوم بإخطارك بالتحديثات الهامة. نشجعك على مراجعة هذه السياسة بشكل دوري.`,
  },
];

export function PrivacyPolicyArClient() {
  const pageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!pageRef.current) return;

    const ctx = gsap.context(() => {
      // Animate title words
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        const letterTimeline = gsap.timeline({ delay: 0.5 });

        shuffledLetters.forEach((letter, index) => {
          letterTimeline.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
          }, index * 0.05);
        });

        // Apply hover effect after animation
        letterTimeline.call(() => {
          applyHoverEffect(letters);
        });
      }

      // Animate content sections
      if (contentRef.current) {
        const sections = contentRef.current.querySelectorAll('.policy-section');
        sections.forEach((section, index) => {
          gsap.set(section, { y: 30, opacity: 0 });
          gsap.to(section, {
            scrollTrigger: {
              trigger: section,
              start: 'top 90%',
            },
            y: 0,
            opacity: 1,
            duration: 0.5,
            delay: index * 0.03,
            ease: 'power3.out',
          });
        });
      }
    }, pageRef);

    return () => ctx.revert();
  }, []);

  // Arabic title split into words for animation (Arabic letters connect, so we animate words)
  const titleWords = 'سياسة الخصوصية'.split(' ');

  return (
    <div ref={pageRef} dir="rtl" className="font-cairo" style={{ backgroundColor: '#0c0c0c' }}>
      {/* Hero Section */}
      <section
        className="relative flex flex-col items-center justify-center text-center"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: '180px',
          paddingBottom: '100px',
        }}
      >
        <div className="content-padding w-full">
          <h1
            ref={titleRef}
            className="hero-title font-cairo text-[calc(1rem+15vw)] leading-[1.1] text-white font-bold"
          >
            {titleWords.map((word, wi) => (
              <span
                key={wi}
                className="letter inline-block mx-[2vw]"
                style={{ transformOrigin: 'center bottom' }}
              >
                {word}
              </span>
            ))}
          </h1>
        </div>

        {/* Description and date */}
        <div className="content-padding w-full" style={{ marginTop: '80px' }}>
          <div className="max-w-4xl mx-auto">
            <div className="max-w-md mr-0 ml-auto">
              <p className="text-white/50 text-lg leading-relaxed text-right">
                خصوصيتك مهمة بالنسبة لنا. توضح هذه السياسة كيف تقوم DataCode بجمع واستخدام وحماية معلوماتك.
              </p>
              <p className="text-white/30 text-sm text-right mt-4">
                تاريخ السريان: ٣٠ مايو ٢٠٢٦
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingBottom: '80px' }}>
        <div className="content-padding">
          <div className="max-w-3xl mx-auto" ref={contentRef}>

            {/* Who We Are */}
            <div className="policy-section pb-12 mb-12" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-2xl font-semibold text-white mb-4 text-right">
                من نحن
              </h2>
              <p className="text-white/60 text-lg leading-relaxed text-right">
                تلتزم DataCode.app بحماية خصوصيتك. توضح سياسة الخصوصية هذه كيفية جمعنا واستخدامنا وحمايتنا لمعلوماتك الشخصية عند استخدام موقعنا الإلكتروني وخدماتنا. باستخدامك لخدماتنا، فإنك توافق على الممارسات الموضحة في هذه السياسة.
              </p>
            </div>

            {/* Policy Sections */}
            {policyContent.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="policy-section pb-10 mb-10"
                style={{
                  borderBottom: index < policyContent.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                    style={{ backgroundColor: '#173835', color: '#fff' }}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-white mb-3 text-right">
                      {section.title}
                    </h2>
                    <p className="text-white/60 leading-relaxed text-right">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Contact Section - Green Background, Centered */}
      <section
        style={{
          backgroundColor: '#173835',
          paddingTop: '100px',
          paddingBottom: '100px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <h2
            className="font-cairo text-[calc(1rem+8vw)] md:text-[calc(1rem+6vw)] leading-[1.1] text-white font-bold"
            style={{ textAlign: 'center', marginBottom: '32px' }}
          >
            تواصل معنا
          </h2>
          <p
            className="text-white/70 text-lg leading-relaxed"
            style={{ textAlign: 'center', maxWidth: '500px', marginBottom: '48px', padding: '0 24px' }}
          >
            إذا كانت لديك أي أسئلة أو استفسارات حول سياسة الخصوصية هذه، فلا تتردد في التواصل معنا.
          </p>

          {/* Contact Info - Centered */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              marginBottom: '48px',
            }}
            className="sm:flex-row sm:gap-10"
          >
            <a
              href="mailto:privacy@datacode.app"
              className="flex items-center gap-3 text-white hover:text-white/80 transition-colors"
              dir="ltr"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>privacy@datacode.app</span>
            </a>

            <span className="hidden sm:block text-white/30">|</span>

            <div className="flex items-center gap-3 text-white/70">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>العراق، أربيل، MRF B2، شقة 80</span>
            </div>
          </div>

          {/* English Version & Back to Home - Centered */}
          <div className="flex flex-col items-center gap-4">
            <Link
              href="/privacy-policy"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm uppercase tracking-wider"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              English Version
            </Link>
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm uppercase tracking-wider"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              العودة للرئيسية
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
